import { BadRequestException, ConflictException, ForbiddenException, Injectable, Logger, NotFoundException } from '@nestjs/common';
import { ConnectionProbeResult, PrismaService, classifyError, errorMessage } from '@cart/api-data-access';
import { AuditService } from '../audit/audit.service';
import { canUseByos } from '../commerce-admin/plan-quota.constants';
import { isByos, readPlatformStorageEnv } from './platform-storage.constants';
import { StorageAdapterResolver } from './storage-adapter-resolver.service';
import { StorageCredentialVault } from './storage-credential.vault';

export interface ConnectByosInput {
  provider: 'r2' | 's3';
  bucket: string;
  region?: string;
  endpoint?: string;
  publicBaseUrl: string;
  pathPrefix?: string;
  accessKeyId: string;
  secretAccessKey: string;
}

export interface StorageConfigSummary {
  id: string;
  mode: string;
  status: string;
  bucket: string;
  region: string;
  publicBaseUrl: string | null;
  healthState: string;
  healthMessage: string | null;
  lastVerifiedAt: Date | null;
  assetCount: number;
  assetBytes: number;
  accessKeyIdMasked: string | null;
  isWriteTarget: boolean;
}

/**
 * Lifecycle of an organization's storage backends.
 *
 * The central idea (docs/19-media-management-plan.md §5): switching storage is
 * a **metadata-only** operation. Configs are never deleted while they still
 * hold assets — they become `retained`, stay readable and deletable, and keep
 * their credentials. That is what lets a merchant move between platform
 * storage and their own bucket as often as they like without a migration and
 * without breaking a single existing image URL.
 */
@Injectable()
export class StorageConfigService {
  private readonly logger = new Logger(StorageConfigService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly vault: StorageCredentialVault,
    private readonly resolver: StorageAdapterResolver,
    private readonly audit?: AuditService
  ) {}

  async listForOrg(organizationId: string): Promise<StorageConfigSummary[]> {
    const configs = await this.prisma.merchantStorageConfig.findMany({
      where: { organizationId, status: { not: 'archived' } },
      orderBy: [{ status: 'asc' }, { createdAt: 'desc' }],
    });
    return configs.map(c => ({
      id: c.id,
      mode: c.mode,
      status: c.status,
      bucket: c.bucket,
      region: c.region,
      publicBaseUrl: c.publicBaseUrl,
      healthState: c.healthState,
      healthMessage: c.healthMessage,
      lastVerifiedAt: c.lastVerifiedAt,
      assetCount: c.assetCount,
      assetBytes: Number(c.assetBytes),
      accessKeyIdMasked: c.accessKeyIdMasked,
      isWriteTarget: c.status === 'active',
    }));
  }

  /**
   * Ensures the org has a platform-managed backend and makes it the write
   * target. Idempotent: re-selecting platform storage after a spell on BYOS
   * reuses the same config row, so assets written before the detour keep
   * resolving to it.
   */
  async usePlatformStorage(organizationId: string, actorId: string): Promise<StorageConfigSummary> {
    const env = readPlatformStorageEnv();
    if (!env) {
      throw new BadRequestException(
        'Platform storage is not configured on this environment (PLATFORM_R2_* variables missing).'
      );
    }

    const existing = await this.prisma.merchantStorageConfig.findFirst({
      where: { organizationId, mode: 'platform_r2' },
    });

    const config =
      existing ??
      (await this.prisma.merchantStorageConfig.create({
        data: {
          organizationId,
          mode: 'platform_r2',
          // Created retained, then promoted by activate() below — the partial
          // unique index allows only one active row per org, so writing
          // 'active' here would collide with the current target.
          status: 'retained',
          bucket: env.bucket,
          region: env.region,
          endpoint: env.endpoint,
          publicBaseUrl: env.publicBaseUrl,
          healthState: 'connected',
          lastVerifiedAt: new Date(),
          createdBy: actorId,
          updatedBy: actorId,
        },
      }));

    await this.activate(organizationId, config.id, actorId);
    const summaries = await this.listForOrg(organizationId);
    return summaries.find(s => s.id === config.id) as StorageConfigSummary;
  }

  /**
   * Guarantees the organization has somewhere to write.
   *
   * Needed because "has a storage backend" is not something a merchant ever
   * sets up: an org created after the R2 cutover, or one that never uploaded
   * an image before the media registry existed, would otherwise fail its first
   * upload with a configuration error it cannot act on.
   *
   * Platform-managed R2 is the default for every organization whenever it is
   * configured — on registration, and on the next upload for any org still on
   * local disk from before the credentials existed.
   *
   * Local disk is only a fallback for an environment with no object storage: a
   * dev box, or production before the cutover. Uploads keep working exactly as
   * they did there, and the moment PLATFORM_R2_* is set the org is promoted to
   * R2 and local disk is demoted to retained. A merchant on their own bucket
   * (BYOS) is never touched — that is their decision, not a default.
   */
  async ensureBackend(organizationId: string, actorId = 'system'): Promise<void> {
    const active = await this.prisma.merchantStorageConfig.findFirst({
      where: { organizationId, status: 'active' },
      select: { id: true, mode: true },
    });

    const platformAvailable = Boolean(readPlatformStorageEnv());

    /*
     * An org already on object storage — platform or its own bucket — is left
     * alone. BYOS especially: that is a merchant's deliberate choice and not
     * something a restart should quietly undo.
     */
    if (active && (active.mode !== 'local_disk' || !platformAvailable)) return;

    /*
     * Falls through to here in two cases, and both want platform R2:
     *
     *   - no backend at all (a merchant who has just registered), and
     *   - an active local-disk backend on an environment that now has R2.
     *
     * The second is the upgrade. Local disk is a fallback for environments
     * without object storage, so an org provisioned before the R2 credentials
     * existed would otherwise keep writing to disk forever — the promotion has
     * to happen on the *next* opportunity, not only at creation. Existing
     * assets are untouched: `MediaAsset.storageConfigId` is immutable, so
     * everything already on disk keeps resolving from disk while new uploads
     * go to R2.
     */
    if (platformAvailable) {
      await this.usePlatformStorage(organizationId, actorId);
      return;
    }

    const existingLocal = await this.prisma.merchantStorageConfig.findFirst({
      where: { organizationId, mode: 'local_disk' },
      select: { id: true },
    });

    if (existingLocal) {
      await this.activate(organizationId, existingLocal.id, actorId);
    } else {
      await this.prisma.merchantStorageConfig.create({
        data: {
          organizationId,
          mode: 'local_disk',
          status: 'active',
          bucket: 'local',
          region: 'auto',
          publicBaseUrl: '/api/uploads',
          healthState: 'connected',
          lastVerifiedAt: new Date(),
          createdBy: actorId,
          updatedBy: actorId,
        },
      });
    }

    this.logger.warn(
      `Organization ${organizationId} provisioned on legacy local-disk storage — ` +
        'set PLATFORM_R2_* and run the local-disk retirement to move it to object storage.'
    );
  }

  /**
   * Validates a merchant's own bucket, then stores it. Validation happens
   * *before* persistence so a saved config has always worked at least once —
   * "connected" on the dashboard should never mean "we hope so".
   */
  async connectByos(
    organizationId: string,
    input: ConnectByosInput,
    actorId: string
  ): Promise<{ config: StorageConfigSummary; probe: ConnectionProbeResult }> {
    const org = await this.prisma.organization.findUnique({
      where: { id: organizationId },
      select: { byosEnabled: true, plan: true },
    });
    if (!org) throw new NotFoundException('Organization not found');

    if (!canUseByos(org.plan, org.byosEnabled)) {
      throw new ForbiddenException(
        'Connecting your own storage bucket is a premium plan feature. ' +
          'Upgrade to premium, or contact support to have it enabled for this account.'
      );
    }

    const mode = input.provider === 's3' ? 'byos_s3' : 'byos_r2';
    const probeAdapter = this.vault.adapterForUnsaved(
      {
        mode,
        bucket: input.bucket,
        region: input.region ?? (input.provider === 'r2' ? 'auto' : 'us-east-1'),
        endpoint: input.endpoint ?? null,
        publicBaseUrl: input.publicBaseUrl,
        pathPrefix: input.pathPrefix ?? null,
        status: 'active',
        updatedAt: new Date(),
      } as never,
      { accessKeyId: input.accessKeyId, secretAccessKey: input.secretAccessKey }
    );

    const probe = await probeAdapter.testConnection();
    if (!probe.ok) {
      throw new BadRequestException({
        message: probe.message ?? 'Could not validate the storage connection.',
        probe,
      });
    }

    const config = await this.prisma.merchantStorageConfig.create({
      data: {
        organizationId,
        mode,
        status: 'retained',
        bucket: input.bucket,
        region: input.region ?? (input.provider === 'r2' ? 'auto' : 'us-east-1'),
        endpoint: input.endpoint ?? null,
        publicBaseUrl: input.publicBaseUrl.replace(/\/+$/, ''),
        pathPrefix: input.pathPrefix ?? null,
        healthState: 'connected',
        lastVerifiedAt: new Date(),
        createdBy: actorId,
        updatedBy: actorId,
      },
    });

    const { accessKeyIdMasked } = await this.vault.store(
      { id: config.id, organizationId },
      { accessKeyId: input.accessKeyId, secretAccessKey: input.secretAccessKey },
      actorId
    );
    await this.prisma.merchantStorageConfig.update({
      where: { id: config.id },
      data: { accessKeyIdMasked },
    });

    await this.activate(organizationId, config.id, actorId);
    await this.log(organizationId, config.id, 'create', { mode, bucket: input.bucket }, actorId);

    const summaries = await this.listForOrg(organizationId);
    return { config: summaries.find(s => s.id === config.id) as StorageConfigSummary, probe };
  }

  /**
   * Flips which backend receives new writes. Nothing is copied and no asset
   * row changes — the previous target is demoted to `retained` and keeps
   * serving everything already on it.
   *
   * Both updates run in one transaction against a partial unique index on
   * (organizationId) WHERE status='active', so two concurrent switches cannot
   * leave the org with two write targets.
   */
  async activate(organizationId: string, configId: string, actorId: string): Promise<void> {
    const target = await this.prisma.merchantStorageConfig.findFirst({
      where: { id: configId, organizationId },
    });
    if (!target) throw new NotFoundException(`Storage config ${configId} not found for this account`);
    if (target.status === 'active') return;

    const previous = await this.prisma.merchantStorageConfig.findFirst({
      where: { organizationId, status: 'active' },
      select: { id: true, mode: true },
    });

    await this.prisma.$transaction(async tx => {
      if (previous) {
        await tx.merchantStorageConfig.update({
          where: { id: previous.id },
          data: { status: 'retained', updatedBy: actorId },
        });
      }
      await tx.merchantStorageConfig.update({
        where: { id: configId },
        data: { status: 'active', updatedBy: actorId },
      });
    });

    this.resolver.invalidate(configId);
    if (previous) this.resolver.invalidate(previous.id);

    await this.log(
      organizationId,
      configId,
      'update',
      { event: 'storage_mode_switched', from: previous?.mode ?? null, to: target.mode },
      actorId
    );
    this.logger.log(
      `Storage write target for org ${organizationId}: ${previous?.mode ?? 'none'} → ${target.mode}`
    );
  }

  /**
   * Disconnect guard (plan §5.2).
   *
   * A retained config that still holds assets cannot be removed: those objects
   * are live on a storefront right now. The merchant's two real options are to
   * copy them elsewhere first or delete them deliberately, and both are their
   * decision to make explicitly rather than a side effect of tidying up a
   * settings screen.
   */
  async disconnect(organizationId: string, configId: string, actorId: string): Promise<void> {
    const config = await this.prisma.merchantStorageConfig.findFirst({
      where: { id: configId, organizationId },
    });
    if (!config) throw new NotFoundException(`Storage config ${configId} not found for this account`);
    if (config.status === 'active') {
      throw new ConflictException(
        'This is your current storage. Switch to another backend before disconnecting it.'
      );
    }

    const liveAssets = await this.prisma.mediaAsset.count({ where: { storageConfigId: configId } });
    if (liveAssets > 0) {
      throw new ConflictException(
        `${liveAssets} file(s) are still stored here and would stop loading. Copy them to your current storage, or delete them permanently, first.`
      );
    }

    await this.prisma.merchantStorageConfig.update({
      where: { id: configId },
      data: { status: 'archived', healthState: 'disconnected', updatedBy: actorId },
    });
    await this.vault.deleteCredentials(configId);
    this.resolver.invalidate(configId);
    await this.log(organizationId, configId, 'update', { event: 'storage_disconnected' }, actorId);
  }

  /** Rotation applies to retained configs too — old media still needs reaching. */
  async rotateCredentials(
    organizationId: string,
    configId: string,
    credentials: { accessKeyId: string; secretAccessKey: string },
    actorId: string
  ): Promise<ConnectionProbeResult> {
    const config = await this.prisma.merchantStorageConfig.findFirst({
      where: { id: configId, organizationId },
    });
    if (!config) throw new NotFoundException(`Storage config ${configId} not found for this account`);
    if (!isByos(config.mode)) {
      throw new BadRequestException('Platform-managed storage credentials are not merchant-rotatable.');
    }

    const probe = await this.vault
      .adapterForUnsaved(config as never, credentials)
      .testConnection();
    if (!probe.ok) {
      throw new BadRequestException({ message: probe.message ?? 'New credentials failed validation.', probe });
    }

    const { accessKeyIdMasked } = await this.vault.store(
      { id: config.id, organizationId },
      credentials,
      actorId
    );
    await this.prisma.merchantStorageConfig.update({
      where: { id: configId },
      data: {
        accessKeyIdMasked,
        healthState: 'connected',
        healthMessage: null,
        lastVerifiedAt: new Date(),
        updatedBy: actorId,
      },
    });

    // Without this the cached adapter keeps signing with the old key until the
    // process restarts.
    this.resolver.invalidate(configId);
    await this.log(organizationId, configId, 'update', { event: 'credentials_rotated' }, actorId);
    return probe;
  }

  /** Re-probes a saved config and records the resulting health state. */
  async verify(configId: string): Promise<ConnectionProbeResult> {
    const config = await this.resolver.configById(configId);
    let probe: ConnectionProbeResult;
    try {
      const adapter = await this.resolver.forConfig(config, 'health_check');
      probe = await adapter.testConnection();
    } catch (err) {
      probe = {
        ok: false,
        canList: false,
        canWrite: false,
        canRead: false,
        canDelete: false,
        publicReadOk: false,
        state: classifyError(err),
        message: errorMessage(err),
      };
    }

    await this.prisma.merchantStorageConfig.update({
      where: { id: configId },
      data: {
        healthState: probe.state,
        healthMessage: probe.message ?? null,
        lastVerifiedAt: new Date(),
      },
    });
    return probe;
  }

  private async log(
    organizationId: string,
    configId: string,
    operation: string,
    payload: Record<string, unknown>,
    actorId: string
  ): Promise<void> {
    await this.audit?.logChange({
      schemaName: 'platform',
      tableName: 'merchant_storage_configs',
      recordId: configId,
      operation,
      // Never the credentials themselves — only the fact that they changed.
      newValue: { organizationId, ...payload },
      changedBy: actorId,
      source: 'storage',
    });
  }
}
